import React, { useMemo, useState } from 'react';

const hojeIso = () => new Date().toISOString().split('T')[0];

const primeiroDiaMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const moeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const numero = (valor, casas = 0) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas
  });

function normalizar(valor) {
  return String(valor || '').trim().toLowerCase();
}

function dataLancamentoFinanceiro(item) {
  return String(item?.vencimento || '').slice(0, 10);
}

function dataContrato(item) {
  return String(item?.data || '').slice(0, 10);
}

function dentroPeriodo(data, inicio, fim) {
  if (!data) return false;
  if (inicio && data < inicio) return false;
  if (fim && data > fim) return false;
  return true;
}

function baixarCSV(nome, colunas, linhas) {
  const esc = (valor) => {
    const txt = String(valor ?? '');
    return `"${txt.replace(/"/g, '""')}"`;
  };

  const conteudo = [
    colunas.map(esc).join(';'),
    ...linhas.map((linha) => linha.map(esc).join(';'))
  ].join('\n');

  const blob = new Blob(["\uFEFF" + conteudo], {
    type: 'text/csv;charset=utf-8;'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function Relatorios({
  frotaList = [],
  motoristasList = [],
  contratosList = [],
  financeiroList = [],
  manutencoesList = [],
  abastecimentosList = []
}) {
  const [aba, setAba] = useState('geral');
  const [dataInicio, setDataInicio] = useState(primeiroDiaMes());
  const [dataFim, setDataFim] = useState(hojeIso());
  const [veiculo, setVeiculo] = useState('');
  const [motorista, setMotorista] = useState('');
  const [cliente, setCliente] = useState('');
  const [status, setStatus] = useState('');

  const frota = Array.isArray(frotaList) ? frotaList : [];
  const motoristas = Array.isArray(motoristasList) ? motoristasList : [];
  const contratos = Array.isArray(contratosList) ? contratosList : [];
  const financeiro = Array.isArray(financeiroList) ? financeiroList : [];
  const manutencoes = Array.isArray(manutencoesList) ? manutencoesList : [];
  const abastecimentos = Array.isArray(abastecimentosList) ? abastecimentosList : [];

  const clientes = useMemo(() => {
    return [...new Set(
      contratos
        .map(c => String(c?.cliente || '').trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [contratos]);

  const dados = useMemo(() => {
    const contratosPeriodo = contratos.filter(c => {
      const data = dataContrato(c);
      if (!dentroPeriodo(data, dataInicio, dataFim)) return false;
      if (veiculo && normalizar(c?.veiculo_id) !== normalizar(veiculo)) return false;
      if (motorista && normalizar(c?.motorista_id) !== normalizar(motorista)) return false;
      if (cliente && normalizar(c?.cliente) !== normalizar(cliente)) return false;
      if (status) {
        const s1 = normalizar(c?.status);
        const s2 = normalizar(c?.status_entrega);
        if (s1 !== normalizar(status) && s2 !== normalizar(status)) return false;
      }
      return true;
    });

    const idsContratoPeriodo = new Set(contratosPeriodo.map(c => String(c.id)));

    const financeiroPeriodo = financeiro.filter(f => {
      const data = dataLancamentoFinanceiro(f);
      if (!dentroPeriodo(data, dataInicio, dataFim)) return false;

      if (veiculo || motorista || cliente) {
        if (String(f?.id || '').startsWith('cont-')) {
          const contratoId = String(f.id).replace('cont-', '');
          return idsContratoPeriodo.has(contratoId);
        }

        if (String(f?.id || '').startsWith('man-')) {
          const manId = String(f.id).replace('man-', '');
          const m = manutencoes.find(x => String(x.id) === manId);
          if (!m) return false;
          if (veiculo && normalizar(m.veiculo) !== normalizar(veiculo)) return false;
        }

        if (String(f?.id || '').startsWith('abas-')) {
          const abasId = String(f.id).replace('abas-', '');
          const a = abastecimentos.find(x => String(x.id) === abasId);
          if (!a) return false;
          if (veiculo && normalizar(a.placa) !== normalizar(veiculo)) return false;
        }
      }

      return true;
    });

    const manutencoesPeriodo = manutencoes.filter(m => {
      if (!dentroPeriodo(String(m?.data || '').slice(0, 10), dataInicio, dataFim)) return false;
      if (veiculo && normalizar(m?.veiculo) !== normalizar(veiculo)) return false;
      return true;
    });

    const abastecimentosPeriodo = abastecimentos.filter(a => {
      const fin = financeiro.find(f => String(f?.id || '') === `abas-${a?.id}`);
      const data = dataLancamentoFinanceiro(fin);
      if (!dentroPeriodo(data, dataInicio, dataFim)) return false;
      if (veiculo && normalizar(a?.placa) !== normalizar(veiculo)) return false;
      return true;
    });

    const receitas = financeiroPeriodo
      .filter(f => normalizar(f?.tipo) === 'receita' || normalizar(f?.tipo) === 'contas a receber')
      .reduce((acc, item) => acc + Number(item?.valor || 0), 0);

    const despesas = financeiroPeriodo
      .filter(f => normalizar(f?.tipo) === 'despesa' || normalizar(f?.tipo) === 'contas a pagar')
      .reduce((acc, item) => acc + Number(item?.valor || 0), 0);

    const resultado = receitas - despesas;
    const margem = receitas > 0 ? (resultado / receitas) * 100 : 0;

    const concluidos = contratosPeriodo.filter(c =>
      normalizar(c?.status) === 'concluído' ||
      normalizar(c?.status_entrega) === 'entregue'
    );

    const ticketMedio = concluidos.length > 0
      ? concluidos.reduce((acc, c) => acc + Number(c?.valor || 0), 0) / concluidos.length
      : 0;

    const porMotorista = {};
    contratosPeriodo.forEach(c => {
      const nome = c?.motorista_id || 'Sem motorista';
      if (!porMotorista[nome]) {
        porMotorista[nome] = {
          motorista: nome,
          viagens: 0,
          entregues: 0,
          receita: 0,
          clientes: new Set(),
          veiculos: new Set()
        };
      }

      porMotorista[nome].viagens += 1;
      if (normalizar(c?.status_entrega) === 'entregue') {
        porMotorista[nome].entregues += 1;
        porMotorista[nome].receita += Number(c?.valor || 0);
      }
      if (c?.cliente) porMotorista[nome].clientes.add(c.cliente);
      if (c?.veiculo_id) porMotorista[nome].veiculos.add(c.veiculo_id);
    });

    const rankingMotoristas = Object.values(porMotorista)
      .map(x => ({
        ...x,
        clientes: x.clientes.size,
        veiculos: [...x.veiculos].join(', ')
      }))
      .sort((a, b) => b.viagens - a.viagens);

    const porCliente = {};
    contratosPeriodo.forEach(c => {
      const nome = c?.cliente || 'Sem cliente';
      if (!porCliente[nome]) {
        porCliente[nome] = {
          cliente: nome,
          contratos: 0,
          entregues: 0,
          faturamento: 0,
          primeira: c?.data || '',
          ultima: c?.data || ''
        };
      }

      const item = porCliente[nome];
      item.contratos += 1;

      if (normalizar(c?.status_entrega) === 'entregue') {
        item.entregues += 1;
        item.faturamento += Number(c?.valor || 0);
      }

      if (c?.data && (!item.primeira || c.data < item.primeira)) item.primeira = c.data;
      if (c?.data && (!item.ultima || c.data > item.ultima)) item.ultima = c.data;
    });

    const rankingClientes = Object.values(porCliente)
      .map(x => ({
        ...x,
        ticket: x.entregues > 0 ? x.faturamento / x.entregues : 0
      }))
      .sort((a, b) => b.faturamento - a.faturamento);

    const porVeiculo = {};
    frota.forEach(f => {
      const placa = f?.placa || 'Sem placa';
      porVeiculo[placa] = {
        placa,
        modelo: f?.modelo || '',
        motorista: f?.motorista || '',
        status: f?.status || '',
        viagens: 0,
        receita: 0,
        combustivel: 0,
        manutencao: 0
      };
    });

    contratosPeriodo.forEach(c => {
      const placa = c?.veiculo_id || 'Sem placa';
      if (!porVeiculo[placa]) {
        porVeiculo[placa] = {
          placa,
          modelo: '',
          motorista: c?.motorista_id || '',
          status: '',
          viagens: 0,
          receita: 0,
          combustivel: 0,
          manutencao: 0
        };
      }

      porVeiculo[placa].viagens += 1;
      if (normalizar(c?.status_entrega) === 'entregue') {
        porVeiculo[placa].receita += Number(c?.valor || 0);
      }
    });

    abastecimentosPeriodo.forEach(a => {
      const placa = a?.placa || 'Sem placa';
      if (!porVeiculo[placa]) {
        porVeiculo[placa] = {
          placa,
          modelo: '',
          motorista: '',
          status: '',
          viagens: 0,
          receita: 0,
          combustivel: 0,
          manutencao: 0
        };
      }
      porVeiculo[placa].combustivel += Number(a?.total || 0);
    });

    manutencoesPeriodo.forEach(m => {
      const placa = m?.veiculo || 'Sem placa';
      if (!porVeiculo[placa]) {
        porVeiculo[placa] = {
          placa,
          modelo: '',
          motorista: '',
          status: '',
          viagens: 0,
          receita: 0,
          combustivel: 0,
          manutencao: 0
        };
      }
      porVeiculo[placa].manutencao += Number(m?.custo || 0);
    });

    const rankingVeiculos = Object.values(porVeiculo)
      .map(v => {
        const custo = v.combustivel + v.manutencao;
        return {
          ...v,
          custo,
          resultado: v.receita - custo,
          custoViagem: v.viagens > 0 ? custo / v.viagens : 0
        };
      })
      .sort((a, b) => b.resultado - a.resultado);

    const combustivel = abastecimentosPeriodo.reduce(
      (acc, a) => acc + Number(a?.total || 0), 0
    );

    const manutencao = manutencoesPeriodo.reduce(
      (acc, m) => acc + Number(m?.custo || 0), 0
    );

    const viagens = contratosPeriodo.length;
    const custoViagem = viagens > 0 ? despesas / viagens : 0;
    const receitaViagem = concluidos.length > 0 ? receitas / concluidos.length : 0;

    return {
      contratosPeriodo,
      financeiroPeriodo,
      manutencoesPeriodo,
      abastecimentosPeriodo,
      receitas,
      despesas,
      resultado,
      margem,
      concluidos,
      ticketMedio,
      rankingMotoristas,
      rankingClientes,
      rankingVeiculos,
      combustivel,
      manutencao,
      viagens,
      custoViagem,
      receitaViagem
    };
  }, [
    contratos,
    financeiro,
    manutencoes,
    abastecimentos,
    frota,
    dataInicio,
    dataFim,
    veiculo,
    motorista,
    cliente,
    status
  ]);

  const limparFiltros = () => {
    setDataInicio(primeiroDiaMes());
    setDataFim(hojeIso());
    setVeiculo('');
    setMotorista('');
    setCliente('');
    setStatus('');
  };

  const exportarAtual = () => {
    if (aba === 'financeiro') {
      baixarCSV(
        `ps_financeiro_${dataInicio}_${dataFim}.csv`,
        ['Tipo', 'Descrição', 'Valor', 'Vencimento', 'Status'],
        dados.financeiroPeriodo.map(f => [
          f.tipo, f.descricao, f.valor, f.vencimento, f.status
        ])
      );
      return;
    }

    if (aba === 'frota') {
      baixarCSV(
        `ps_frota_${dataInicio}_${dataFim}.csv`,
        ['Placa', 'Modelo', 'Motorista', 'Viagens', 'Receita', 'Combustível', 'Manutenção', 'Custo', 'Resultado'],
        dados.rankingVeiculos.map(v => [
          v.placa, v.modelo, v.motorista, v.viagens, v.receita,
          v.combustivel, v.manutencao, v.custo, v.resultado
        ])
      );
      return;
    }

    if (aba === 'motoristas') {
      baixarCSV(
        `ps_motoristas_${dataInicio}_${dataFim}.csv`,
        ['Motorista', 'Viagens', 'Entregas', 'Receita associada', 'Clientes', 'Veículos'],
        dados.rankingMotoristas.map(m => [
          m.motorista, m.viagens, m.entregues, m.receita, m.clientes, m.veiculos
        ])
      );
      return;
    }

    if (aba === 'clientes') {
      baixarCSV(
        `ps_clientes_${dataInicio}_${dataFim}.csv`,
        ['Cliente', 'Contratos', 'Entregas', 'Faturamento', 'Ticket médio', 'Primeira operação', 'Última operação'],
        dados.rankingClientes.map(c => [
          c.cliente, c.contratos, c.entregues, c.faturamento, c.ticket, c.primeira, c.ultima
        ])
      );
      return;
    }

    if (aba === 'contratos') {
      baixarCSV(
        `ps_contratos_${dataInicio}_${dataFim}.csv`,
        ['Cliente', 'Origem', 'Destino', 'Veículo', 'Motorista', 'Data', 'Valor', 'Status', 'Entrega', 'Rastreio'],
        dados.contratosPeriodo.map(c => [
          c.cliente, c.origem, c.destino, c.veiculo_id, c.motorista_id,
          c.data, c.valor, c.status, c.status_entrega, c.codigo_rastreio
        ])
      );
      return;
    }

    if (aba === 'abastecimento') {
      baixarCSV(
        `ps_abastecimento_${dataInicio}_${dataFim}.csv`,
        ['Placa', 'Odômetro', 'Litros', 'Valor', 'Posto'],
        dados.abastecimentosPeriodo.map(a => [
          a.placa, a.odometro, a.litros, a.total, a.posto
        ])
      );
      return;
    }

    if (aba === 'manutencao') {
      baixarCSV(
        `ps_manutencao_${dataInicio}_${dataFim}.csv`,
        ['Veículo', 'Tipo', 'Descrição', 'Data', 'Odômetro', 'Custo', 'Situação'],
        dados.manutencoesPeriodo.map(m => [
          m.veiculo, m.tipo, m.descricao, m.data, m.odometro, m.custo, m.situacao
        ])
      );
      return;
    }

    baixarCSV(
      `ps_resumo_geral_${dataInicio}_${dataFim}.csv`,
      ['Indicador', 'Valor'],
      [
        ['Receitas', dados.receitas],
        ['Despesas', dados.despesas],
        ['Resultado', dados.resultado],
        ['Margem %', numero(dados.margem, 2)],
        ['Viagens', dados.viagens],
        ['Entregas concluídas', dados.concluidos.length],
        ['Ticket médio', dados.ticketMedio],
        ['Combustível', dados.combustivel],
        ['Manutenção', dados.manutencao]
      ]
    );
  };

  const tabs = [
    ['geral', 'Visão Geral'],
    ['financeiro', 'Financeiro'],
    ['frota', 'Frota'],
    ['motoristas', 'Motoristas'],
    ['clientes', 'Clientes'],
    ['contratos', 'Contratos'],
    ['abastecimento', 'Abastecimento'],
    ['manutencao', 'Manutenção']
  ];

  const card = {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: '10px',
    padding: '18px'
  };

  const input = {
    width: '100%',
    padding: '10px 11px',
    background: '#181818',
    color: '#fff',
    border: '1px solid #353535',
    borderRadius: '7px',
    boxSizing: 'border-box'
  };

  const th = {
    padding: '10px 12px',
    textAlign: 'left',
    color: '#999',
    fontSize: '11px',
    borderBottom: '1px solid #333',
    whiteSpace: 'nowrap'
  };

  const td = {
    padding: '10px 12px',
    color: '#ddd',
    fontSize: '12px',
    borderBottom: '1px solid #252525',
    verticalAlign: 'top'
  };

  const tabela = (cabecalhos, linhas) => (
    <div style={{ overflowX: 'auto', border: '1px solid #252525', borderRadius: '8px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
        <thead>
          <tr>{cabecalhos.map(h => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>{linhas}</tbody>
      </table>
    </div>
  );

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: '11px', color: '#777', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '800' }}>
            Inteligência & Gestão
          </span>
          <h2 style={{ margin: '4px 0 4px', fontSize: '25px' }}>Relatórios Gerenciais</h2>
          <p style={{ color: '#777', margin: 0, fontSize: '12px' }}>
            Análise financeira, operacional, frota, motoristas, clientes e custos.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={exportarAtual} style={{ padding: '10px 14px', border: 0, borderRadius: '7px', background: '#32D74B', color: '#000', fontWeight: '800', cursor: 'pointer' }}>
            Exportar CSV
          </button>
          <button onClick={() => window.print()} style={{ padding: '10px 14px', border: '1px solid #444', borderRadius: '7px', background: '#171717', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>
            Imprimir / PDF
          </button>
        </div>
      </div>

      <div style={{ ...card, marginBottom: '18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>DATA INICIAL</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={input} />
          </div>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>DATA FINAL</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={input} />
          </div>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>VEÍCULO</label>
            <select value={veiculo} onChange={e => setVeiculo(e.target.value)} style={input}>
              <option value="">Todos</option>
              {frota.map(f => <option key={f.id || f.placa} value={f.placa}>{f.placa} - {f.modelo}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>MOTORISTA</label>
            <select value={motorista} onChange={e => setMotorista(e.target.value)} style={input}>
              <option value="">Todos</option>
              {motoristas.map(m => <option key={m.id || m.nome} value={m.nome}>{m.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>CLIENTE</label>
            <select value={cliente} onChange={e => setCliente(e.target.value)} style={input}>
              <option value="">Todos</option>
              {clientes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '10px', color: '#888' }}>STATUS</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={input}>
              <option value="">Todos</option>
              <option value="Ativo">Ativo</option>
              <option value="Concluído">Concluído</option>
              <option value="Aguardando coleta">Aguardando coleta</option>
              <option value="Em transporte">Em transporte</option>
              <option value="Entregue">Entregue</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <button onClick={limparFiltros} style={{ padding: '9px 13px', border: '1px solid #444', borderRadius: '7px', background: '#1b1b1b', color: '#bbb', cursor: 'pointer' }}>
            Limpar filtros
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '18px', paddingBottom: '4px' }}>
        {tabs.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setAba(id)}
            style={{
              padding: '9px 13px',
              borderRadius: '7px',
              border: aba === id ? '1px solid #007AFF' : '1px solid #303030',
              background: aba === id ? '#0b2844' : '#151515',
              color: aba === id ? '#fff' : '#999',
              fontWeight: aba === id ? '800' : '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {aba === 'geral' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '18px' }}>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>RECEITAS</span><div style={{ color: '#32D74B', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{moeda(dados.receitas)}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>DESPESAS</span><div style={{ color: '#FF453A', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{moeda(dados.despesas)}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>RESULTADO</span><div style={{ color: dados.resultado >= 0 ? '#32D74B' : '#FF453A', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{moeda(dados.resultado)}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>MARGEM</span><div style={{ color: '#FFD60A', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{numero(dados.margem, 1)}%</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>VIAGENS / CONTRATOS</span><div style={{ color: '#fff', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{dados.viagens}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>ENTREGAS</span><div style={{ color: '#32D74B', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{dados.concluidos.length}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>TICKET MÉDIO</span><div style={{ color: '#007AFF', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{moeda(dados.ticketMedio)}</div></div>
            <div style={card}><span style={{ color: '#888', fontSize: '10px' }}>CUSTO / VIAGEM</span><div style={{ color: '#FF9F0A', fontWeight: '900', fontSize: '22px', marginTop: '8px' }}>{moeda(dados.custoViagem)}</div></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
            <div style={card}>
              <h3 style={{ fontSize: '13px', marginTop: 0 }}>Top Motoristas por Viagens</h3>
              {dados.rankingMotoristas.slice(0, 5).map((m, i) => (
                <div key={m.motorista} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #252525' }}>
                  <span>{i + 1}. {m.motorista}</span>
                  <b>{m.viagens} viagens</b>
                </div>
              ))}
            </div>

            <div style={card}>
              <h3 style={{ fontSize: '13px', marginTop: 0 }}>Top Clientes por Faturamento</h3>
              {dados.rankingClientes.slice(0, 5).map((c, i) => (
                <div key={c.cliente} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '9px 0', borderBottom: '1px solid #252525' }}>
                  <span>{i + 1}. {c.cliente}</span>
                  <b style={{ color: '#32D74B' }}>{moeda(c.faturamento)}</b>
                </div>
              ))}
            </div>

            <div style={card}>
              <h3 style={{ fontSize: '13px', marginTop: 0 }}>Custos Operacionais</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0' }}><span>Combustível</span><b>{moeda(dados.combustivel)}</b></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0' }}><span>Manutenção</span><b>{moeda(dados.manutencao)}</b></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderTop: '1px solid #333' }}><span>Participação nos gastos</span><b>{dados.despesas > 0 ? numero(((dados.combustivel + dados.manutencao) / dados.despesas) * 100, 1) : '0,0'}%</b></div>
            </div>
          </div>
        </>
      )}

      {aba === 'financeiro' && tabela(
        ['Tipo', 'Descrição', 'Valor', 'Vencimento', 'Status'],
        dados.financeiroPeriodo.map(f => (
          <tr key={f.id}>
            <td style={td}><span style={{ color: normalizar(f.tipo).includes('receita') ? '#32D74B' : '#FF453A', fontWeight: '800' }}>{f.tipo}</span></td>
            <td style={td}>{f.descricao}</td>
            <td style={td}>{moeda(f.valor)}</td>
            <td style={td}>{f.vencimento}</td>
            <td style={td}>{f.status}</td>
          </tr>
        ))
      )}

      {aba === 'frota' && tabela(
        ['Placa', 'Modelo', 'Motorista', 'Viagens', 'Receita', 'Combustível', 'Manutenção', 'Custo', 'Resultado'],
        dados.rankingVeiculos.map(v => (
          <tr key={v.placa}>
            <td style={td}><b>{v.placa}</b></td>
            <td style={td}>{v.modelo}</td>
            <td style={td}>{v.motorista}</td>
            <td style={td}>{v.viagens}</td>
            <td style={td}>{moeda(v.receita)}</td>
            <td style={td}>{moeda(v.combustivel)}</td>
            <td style={td}>{moeda(v.manutencao)}</td>
            <td style={td}>{moeda(v.custo)}</td>
            <td style={{ ...td, color: v.resultado >= 0 ? '#32D74B' : '#FF453A', fontWeight: '800' }}>{moeda(v.resultado)}</td>
          </tr>
        ))
      )}

      {aba === 'motoristas' && tabela(
        ['Motorista', 'Viagens', 'Entregas', 'Receita associada', 'Clientes atendidos', 'Veículos'],
        dados.rankingMotoristas.map(m => (
          <tr key={m.motorista}>
            <td style={td}><b>{m.motorista}</b></td>
            <td style={td}>{m.viagens}</td>
            <td style={td}>{m.entregues}</td>
            <td style={td}>{moeda(m.receita)}</td>
            <td style={td}>{m.clientes}</td>
            <td style={td}>{m.veiculos || '-'}</td>
          </tr>
        ))
      )}

      {aba === 'clientes' && tabela(
        ['Cliente', 'Contratos', 'Entregas', 'Faturamento', 'Ticket médio', 'Primeira operação', 'Última operação'],
        dados.rankingClientes.map(c => (
          <tr key={c.cliente}>
            <td style={td}><b>{c.cliente}</b></td>
            <td style={td}>{c.contratos}</td>
            <td style={td}>{c.entregues}</td>
            <td style={td}>{moeda(c.faturamento)}</td>
            <td style={td}>{moeda(c.ticket)}</td>
            <td style={td}>{c.primeira || '-'}</td>
            <td style={td}>{c.ultima || '-'}</td>
          </tr>
        ))
      )}

      {aba === 'contratos' && tabela(
        ['Cliente', 'Origem / Destino', 'Veículo / Motorista', 'Data', 'Valor', 'Entrega', 'Contrato', 'Rastreio'],
        dados.contratosPeriodo.map(c => (
          <tr key={c.id}>
            <td style={td}><b>{c.cliente}</b></td>
            <td style={td}>{c.origem} → {c.destino}</td>
            <td style={td}>{c.veiculo_id || '-'} / {c.motorista_id || '-'}</td>
            <td style={td}>{c.data}</td>
            <td style={td}>{moeda(c.valor)}</td>
            <td style={td}>{c.status_entrega}</td>
            <td style={td}>{c.status}</td>
            <td style={td}>{c.codigo_rastreio || '-'}</td>
          </tr>
        ))
      )}

      {aba === 'abastecimento' && tabela(
        ['Placa', 'Odômetro', 'Litros', 'Valor', 'Posto'],
        dados.abastecimentosPeriodo.map(a => (
          <tr key={a.id}>
            <td style={td}><b>{a.placa}</b></td>
            <td style={td}>{a.odometro}</td>
            <td style={td}>{a.litros}</td>
            <td style={td}>{moeda(a.total)}</td>
            <td style={td}>{a.posto}</td>
          </tr>
        ))
      )}

      {aba === 'manutencao' && tabela(
        ['Veículo', 'Tipo', 'Descrição', 'Data', 'Odômetro', 'Custo', 'Situação'],
        dados.manutencoesPeriodo.map(m => (
          <tr key={m.id}>
            <td style={td}><b>{m.veiculo}</b></td>
            <td style={td}>{m.tipo}</td>
            <td style={td}>{m.descricao}</td>
            <td style={td}>{m.data}</td>
            <td style={td}>{m.odometro}</td>
            <td style={td}>{moeda(m.custo)}</td>
            <td style={td}>{m.situacao}</td>
          </tr>
        ))
      )}
    </div>
  );
}
